import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import Cookies from 'js-cookie';
import { adminAPI } from '../services/adminApi';
import toast from 'react-hot-toast';
import { useAuth } from './AuthContext';

const LOCATION_COOKIE_OPTIONS = {
  expires: 365,
  sameSite: 'strict',
  secure: false,
  path: '/'
};

const toIdString = (value) => {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (typeof value === 'object') {
    if (value._id != null) return toIdString(value._id);
    if (value.id != null) return toIdString(value.id);
  }
  return String(value);
};

const getAssignmentLocationId = (assignment) => {
  const loc = assignment?.locationId;
  if (loc == null) return '';
  if (typeof loc === 'string' || typeof loc === 'number') return String(loc);
  return toIdString(loc._id || loc.id);
};

const getAssignmentLocationDoc = (assignment) => {
  const loc = assignment?.locationId;
  if (!loc || typeof loc === 'string' || typeof loc === 'number') return null;
  if (!loc._id && !loc.id) return null;
  return loc;
};

const persistLocationId = (locationId) => {
  if (!locationId) return;
  const id = toIdString(locationId);
  if (!id) return;
  localStorage.setItem('lastUsedLocationId', id);
  localStorage.setItem('X-Location-Id', id);
  Cookies.set('currentLocationId', id, LOCATION_COOKIE_OPTIONS);
};

const clearPersistedLocationId = () => {
  localStorage.removeItem('lastUsedLocationId');
  localStorage.removeItem('X-Location-Id');
  Cookies.remove('currentLocationId');
};

const readSavedLocationId = () =>
  localStorage.getItem('lastUsedLocationId')
  || localStorage.getItem('X-Location-Id')
  || Cookies.get('currentLocationId')
  || '';

// Safe fallback used when the context is read before the Provider has mounted
// (e.g. during Chrome tab-duplication's partial state restore).
// isLoading:true keeps UI in a neutral "loading" state until the real Provider hydrates.
const defaultContextValue = {
  currentLocation: null,
  availableLocations: [],
  isLoading: true,
  error: null,
  switchLocation: () => false,
  loadAvailableLocations: async () => {},
  getLocationById: () => null,
  hasLocationAccess: () => false,
  clearLocationContext: () => {},
  handleExplicitLogout: () => {},
  hasMultipleLocations: false,
  locationCount: 0
};

const LocationContext = createContext(defaultContextValue);

export const useLocation = () => useContext(LocationContext);

export const LocationProvider = ({ children }) => {
  const { user, isAuthenticated, isLoading: authIsLoading, token } = useAuth();
  const [currentLocation, setCurrentLocation] = useState(null);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Ignore stale responses when auth/user changes trigger overlapping fetches.
  const loadGenerationRef = useRef(0);
  const userId = toIdString(user?._id || user?.id);

  const applyLocation = useCallback((locationDoc) => {
    if (!locationDoc) {
      setCurrentLocation(null);
      return;
    }
    const locationId = toIdString(locationDoc._id || locationDoc.id);
    setCurrentLocation(locationDoc);
    persistLocationId(locationId);
  }, []);

  // Select default location (isDefault=true or first available with a populated doc).
  // NOTE: Only call when there is NO saved location preference.
  const selectDefaultLocation = useCallback((locations) => {
    const usable = (locations || []).filter((loc) => getAssignmentLocationDoc(loc));
    if (usable.length === 0) {
      // No assignments — clear any stale stored location so UIs don't keep
      // treating a previous session's X-Location-Id as valid, and never spin forever.
      setCurrentLocation(null);
      clearPersistedLocationId();
      return;
    }

    const defaultLocation = usable.find(
      (loc) => loc.isDefault === true || loc.locationId?.settings?.isDefault === true
    ) || usable[0];

    applyLocation(getAssignmentLocationDoc(defaultLocation));
  }, [applyLocation]);

  const loadAvailableLocations = useCallback(async () => {
    if (!isAuthenticated || !userId) {
      setIsLoading(false);
      return;
    }

    const generation = ++loadGenerationRef.current;
    const isStale = () => generation !== loadGenerationRef.current;

    // Keep API header warm from last session while the network request runs.
    const savedLocationId = readSavedLocationId();
    if (savedLocationId) {
      localStorage.setItem('X-Location-Id', savedLocationId);
    }

    const fetchOnce = async () => {
      const response = await adminAPI.getUserLocations(userId);
      return response.data;
    };

    try {
      setIsLoading(true);
      setError(null);

      let payload = null;
      let lastError = null;
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          payload = await fetchOnce();
          lastError = null;
          break;
        } catch (err) {
          lastError = err;
          // Brief backoff before retry — covers gateway/auth race on hard reload.
          if (attempt === 0) {
            await new Promise((resolve) => setTimeout(resolve, 350));
            if (isStale()) return;
          }
        }
      }

      if (isStale()) return;

      if (lastError) {
        throw lastError;
      }

      if (payload?.success) {
        // Filter out assignments where the location itself is inactive
        // (inactive locations return locationId: null from the populate match filter)
        const locations = Array.isArray(payload.data)
          ? payload.data.filter((loc) => loc.locationId != null)
          : [];
        setAvailableLocations(locations);

        if (locations.length === 0) {
          selectDefaultLocation(locations);
        } else if (savedLocationId) {
          const savedAssignment = locations.find(
            (loc) => getAssignmentLocationId(loc) === savedLocationId
          );
          const savedDoc = savedAssignment ? getAssignmentLocationDoc(savedAssignment) : null;

          if (savedDoc) {
            applyLocation(savedDoc);
          } else {
            // Saved preference no longer valid — pick a default (also clears stale ids if none usable)
            selectDefaultLocation(locations);
          }
        } else {
          selectDefaultLocation(locations);
        }
      } else {
        setError(payload?.message || 'Failed to load locations');
        setCurrentLocation(null);
      }
    } catch (err) {
      if (isStale()) return;
      console.error('Error loading locations:', err);
      setError(err.response?.data?.message || 'Failed to load locations');
      toast.error('Failed to load locations');
      // Stop loading so pages don't spin forever; leave any prior location if we had one.
    } finally {
      // Always end loading for the active generation so users with zero locations
      // (or a failed fetch) never get stuck on a spinner.
      if (!isStale()) {
        setIsLoading(false);
      }
    }
  }, [isAuthenticated, userId, applyLocation, selectDefaultLocation]);

  const switchLocation = useCallback((locationId) => {
    const normalizedId = toIdString(locationId);
    const locationToSwitch = availableLocations.find(
      (loc) => getAssignmentLocationId(loc) === normalizedId
    );

    if (!locationToSwitch) {
      toast.error('Location not found or not accessible');
      return false;
    }

    persistLocationId(normalizedId);

    // DO NOT update React state here — reload re-hydrates from storage.
    toast.success(`Switching to ${locationToSwitch.locationId.name}...`);

    setTimeout(() => {
      window.location.reload();
    }, 100);

    return true;
  }, [availableLocations]);

  const getLocationById = useCallback((locationId) => {
    const normalizedId = toIdString(locationId);
    const found = availableLocations.find(
      (loc) => getAssignmentLocationId(loc) === normalizedId
    );
    return getAssignmentLocationDoc(found) || null;
  }, [availableLocations]);

  const hasLocationAccess = useCallback((locationId) => {
    const normalizedId = toIdString(locationId);
    return availableLocations.some(
      (loc) => getAssignmentLocationId(loc) === normalizedId
    );
  }, [availableLocations]);

  const clearLocationContext = useCallback(() => {
    console.log('Clearing location context (explicit logout)');
    loadGenerationRef.current += 1;
    setCurrentLocation(null);
    setAvailableLocations([]);
    clearPersistedLocationId();
  }, []);

  const handleExplicitLogout = useCallback(() => {
    sessionStorage.setItem('explicitLogout', 'true');
    clearLocationContext();
  }, [clearLocationContext]);

  useEffect(() => {
    if (isAuthenticated && userId && token) {
      loadAvailableLocations();
    } else if (!isAuthenticated && !userId && !authIsLoading) {
      const isExplicitLogout = sessionStorage.getItem('explicitLogout') === 'true';

      if (isExplicitLogout) {
        sessionStorage.removeItem('explicitLogout');
        clearLocationContext();
      } else {
        console.log('Session expired, preserving location preference');
      }
      setIsLoading(false);
    } else {
      // Auth is still loading — keep consumers in loading state
      setIsLoading(true);
    }
  }, [isAuthenticated, userId, token, authIsLoading, loadAvailableLocations, clearLocationContext]);

  // Keep storage in sync when current location changes after load
  useEffect(() => {
    if (currentLocation) {
      persistLocationId(currentLocation._id || currentLocation.id);
    }
  }, [currentLocation]);

  const value = {
    currentLocation,
    availableLocations,
    isLoading,
    error,
    switchLocation,
    loadAvailableLocations,
    getLocationById,
    hasLocationAccess,
    clearLocationContext,
    handleExplicitLogout,
    hasMultipleLocations: availableLocations.length > 1,
    locationCount: availableLocations.length
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};

export default LocationContext;
